import { useLazyGetFirmRemainderReportLazyQuery } from "../../../store/storageRoles/reportsApi";
import useAuth from "../../../Hooks/useAuth";
import { useEffect, useState, useMemo } from "react";
import ReportLayout from "../../../Layout/Reports/ReportLayout";
import ReportFilterCard from "../../../Components/Reports/ReportFilterCard";
import ReportLoadingState from "../../../Components/Reports/ReportLoadingState";
import ReportErrorState from "../../../Components/Reports/ReportErrorState";
import ReportPdfPreview, {
  downloadPdfFromBase64,
} from "../../../Components/Reports/ReportPdfPreview";
import ReportEmptyState from "../../../Components/Reports/ReportEmptyState";
import useReportFirms from "../../../Hooks/useReportFirms";
import {
  ProductDropdown,
  FirmDropdown,
  NumberInput,
} from "../../../Components/atoms";
import { buildExcelConfig } from "../../../Components/Reports/reportExcelDefaults";
import { FaFilePdf, FaDownload, FaTimes } from "react-icons/fa";
import { parentFirmLabel } from "../../../utils/firmTypeStyles";

export default function ReportFirmRemainderPage() {
  const { auth } = useAuth();
  const [playMakerId, setPlayMakerId] = useState("");
  const [productName, setProductName] = useState("");
  const [lowerLimit, setLowerLimit] = useState("");
  const [upperLimit, setUpperLimit] = useState("");
  // Çoklu firma seçimi (Müşteriler Yıllık Performans Raporu ile aynı desen);
  // seçilen firmaların alt firmaları sunucuda ağaç olarak çözülür.
  const [selectedFirms, setSelectedFirms] = useState([]);
  const [productNameError, setProductNameError] = useState(false);
  const [firmNameError, setFirmNameError] = useState(false);
  const [parsedData, setParsedData] = useState([]);
  const token = auth.accessToken;

  // Rapor yalnız AKTİF müşteri/bayileri kapsıyor (sunucu da `isActive: true`
  // filtreliyor) — pasif ya da depo firmayı seçtirmek boş rapor üretiyordu.
  const { firms: activeFirms, isLoading: firmDataIsLoading } = useReportFirms(auth.accessToken, {
    includeInactive: false,
  });

  const [
    getFirmRemainderReport,
    { data: firmRemainderReport, isLoading, isFetching, error },
  ] = useLazyGetFirmRemainderReportLazyQuery();

  // RTK Query'de `isLoading` YALNIZCA ilk yüklemede true olur; aynı sayfada
  // "Raporu Getir"e tekrar basınca istek gitse bile loading görünmüyordu.
  // Bekleme durumu için `isFetching` de hesaba katılır.
  const isBusy = isLoading || isFetching;

  // Statik ürün listesi
  const productTypes = useMemo(
    () => [
      { id: 1, productTypeName: "Türk Kahvesi" },
      { id: 2, productTypeName: "Çay" },
      { id: 3, productTypeName: "Filtre Kahve" },
      { id: 4, productTypeName: "Espresso" },
    ],
    []
  );

  useEffect(() => {
    if (productTypes.length > 0) {
      setProductName(productTypes[0].productTypeName);
    }
  }, [productTypes]);

  useEffect(() => {
    if (firmRemainderReport && firmRemainderReport.data) {
      const allProductNames = new Set();
      const parsed = firmRemainderReport.data.map((item) => {
        allProductNames.add(item.productName);
        return {
          firmName: item.firmName,
          mainFirmName: item.mainFirmName,
          mainFirmType: item.mainFirmType ?? null,
          productName: item.productName,
          // Toplam bakiye = cihazlardaki + dağıtılmamış (sözleşme
          // firm-remainder-report 1.0, Workspace #152).
          quota: item.quota,
          deviceQuota: item.deviceQuota ?? 0,
          undistributedQuota: item.undistributedQuota ?? 0,
        };
      });
      setParsedData(parsed);
    }
  }, [firmRemainderReport]);

  const handleSearch = () => {
    if (!productName) {
      setProductNameError(true);
      return;
    }

    // Firma seçimi zorunlu: seçimsiz istek tüm portföyü tek PDF'e basıyordu.
    if (selectedFirms.length === 0) {
      setFirmNameError(true);
      return;
    }

    getFirmRemainderReport({
      token,
      playMakerId: playMakerId || "undefined",
      productName: productName || "undefined",
      // Server validasyonu limit alanlarını string bekliyor (NumberInput number verir)
      lowerLimit: lowerLimit === "" ? "0" : String(lowerLimit),
      upperLimit: upperLimit === "" ? "0" : String(upperLimit),
      firmIds: selectedFirms.map((f) => f._id),
    });
  };

  const handleFirmChange = (nextFirms) => {
    setSelectedFirms(Array.isArray(nextFirms) ? nextFirms : []);
    setFirmNameError(false);
  };

  const handleClearFilters = () => {
    setPlayMakerId("");
    setProductName("");
    setLowerLimit("");
    setUpperLimit("");
    setSelectedFirms([]);
    setProductNameError(false);
    setFirmNameError(false);
    setParsedData([]);
  };



  const formatExcelData = (data) => {
    if (!data) return [];

    return data.map((item) => ({
      "Firma Adı": item.firmName,
      "Ana Firma Adı": parentFirmLabel(item.mainFirmName, item.mainFirmType),
      "Ürün Adı": item.productName,
      "Cihazlardaki Kota": item.deviceQuota,
      "Dağıtılmamış Kota": item.undistributedQuota,
      "Toplam Bakiye": item.quota,
    }));
  };

  const pdfBase64 = firmRemainderReport?.pdfBase64;

  const headerActions = pdfBase64 ? (
    <button
      onClick={() => downloadPdfFromBase64(pdfBase64, firmRemainderReport?.fileName)}
      title="PDF İndir"
      className="flex items-center gap-1.5 px-2.5 py-2 sm:px-3.5 bg-red-600 text-white rounded-lg shadow-sm hover:bg-red-700 active:bg-red-800 transition-colors text-xs font-semibold"
    >
      <FaFilePdf className="w-4 h-4" />
      <FaDownload className="w-3 h-3" />
      <span className="hidden sm:inline">PDF İndir</span>
    </button>
  ) : null;

  const excelConfig = buildExcelConfig({
    sheetName: "Firma Bakiye Seviye Raporu",
    widths: [25, 25, 18, 16, 18, 15],
    numericColumns: ["deviceQuota", "undistributedQuota", "quota"],
    columnMap: {
      firmName: 0,
      mainFirmName: 1,
      productName: 2,
      deviceQuota: 3,
      undistributedQuota: 4,
      quota: 5,
    },
  });

  return (
    <ReportLayout
      title="Müşteri Bakiye Seviyeleri Raporu"
      excelData={formatExcelData(parsedData)}
      fileName={`Firma_Bakiye_Raporu_${new Date()
        .toLocaleDateString("tr-TR")
        .replace(/\./g, "_")}`}
      excelConfig={excelConfig}
      headerActions={headerActions}
      sidebar={
        <ReportFilterCard
        title="Fatura Filtreleme"
        description="Rapor için filtre seçeneklerini belirleyin"
        showDatePickers={false}
        onSearch={handleSearch}
        onClear={handleClearFilters}
        isLoading={isBusy}
        searchButtonText="Raporu Getir"
        clearButtonText="Temizle"
        searchDisabled={!productName || selectedFirms.length === 0}
        searchDisabledMessage="Ürün ve en az bir firma seçiniz"
        activeFilters={[
        { label: "Ürün", value: productName },
        { label: "Alt Limit", value: lowerLimit === "" ? "" : String(lowerLimit) },
        { label: "Üst Limit", value: upperLimit === "" ? "" : String(upperLimit) },
        {
        label: "Firma",
        value: selectedFirms.length > 0 ? `${selectedFirms.length} firma` : "",
        },
        ]}
        >
        {/* Performans raporlarıyla AYNI yerleşim: 2 kolon, aynı boşluk —
        firma dropdown'ı orada da burada da aynı genişlikte görünsün. */}
        <div className="grid grid-cols-1 gap-4">
        {/* Ürün Adı */}
        <ProductDropdown
        value={productName}
        onChange={(product) => {
        // Ürün değişince firma listesi de değişiyor (ürünü pasif olan
        // firmalar düşüyor); eski seçim artık listede olmayabilir, bu
        // yüzden seçim sıfırlanır.
        setProductName(product);
        setSelectedFirms([]);
        setProductNameError(false);
        }}
        products={productTypes}
        error={productNameError}
        errorMessage="Ürün adı seçiniz"
        />

        {/* Alt Limit */}
        <NumberInput
        label="Alt Limit"
        placeholder="Alt limit girin..."
        value={lowerLimit}
        onChange={(value) => setLowerLimit(value)}
        min={0}
        />

        {/* Üst Limit */}
        <NumberInput
        label="Üst Limit"
        placeholder="Üst limit girin..."
        value={upperLimit}
        onChange={(value) => setUpperLimit(value)}
        min={0}
        />

        {/* Firma */}
        <FirmDropdown
        label="Firmalar"
        multiple
        required
        value={selectedFirms}
        onChange={handleFirmChange}
        firms={activeFirms}
        loading={firmDataIsLoading}
        error={firmNameError}
        errorMessage="En az bir firma seçiniz"
        placeholder="Firma seçin"
        />
        </div>

        {/* Seçilen firmalar — TEK satır, yatay kayar; uzun adlar "..." ile
        kesilir (yıllık performans raporuyla aynı desen). */}
        {selectedFirms.length > 0 && (
        <div className="mt-4 flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 py-0.5">
        {selectedFirms.map((firm) => (
        <span
        key={firm._id}
        title={firm.firmName}
        className="inline-flex shrink-0 items-center gap-1.5 max-w-[200px] pl-3 pr-2 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs font-semibold"
        >
        <span className="truncate">{firm.firmName}</span>
        <button
        type="button"
        onClick={() =>
        setSelectedFirms((prev) => prev.filter((f) => f._id !== firm._id))
        }
        className="w-4 h-4 shrink-0 rounded-full flex items-center justify-center hover:bg-blue-200 transition-colors"
        aria-label={`${firm.firmName} seçimini kaldır`}
        >
        <FaTimes className="w-2.5 h-2.5" />
        </button>
        </span>
        ))}
        </div>
        )}
        </ReportFilterCard>
      }
    >
      {/* Loading State */}
      {isBusy && (
        <ReportLoadingState
          title="Firma Bakiye Raporu Hazırlanıyor"
          description="Veriler yükleniyor, lütfen bekleyin..."
          progressText="Veriler işleniyor..."
          skeletonRows={5}
          skeletonColumns={3}
        />
      )}
      {/* Error State */}
      {error && (
        <ReportErrorState
          title="Hata Oluştu"
          message={error.message || "Rapor yüklenirken bir hata oluştu."}
          icon="warning"
        />
      )}

      {/* PDF önizleme — rapor artık sunucuda tasarlanmış PDF olarak üretilir */}
      {!isBusy && !error && pdfBase64 && (
        <ReportPdfPreview
          pdfBase64={pdfBase64}
          title={`Müşteri Bakiye Seviyeleri Raporu — ${parsedData.length} firma`}
        />
      )}

      {/* Empty State */}
      {!isBusy && !error && !pdfBase64 && (
        <ReportEmptyState
          title="Veri Bulunamadı"
          message="Seçilen kriterlere uygun firma verisi bulunamadı."
          icon="document"
        />
      )}
    </ReportLayout>
  );
}
